import type { Country } from './arrays';

export type SelectOption = {
	value: string;
	text: string;
};

const FIRST_BIRTH_YEAR = 1930;
const MAX_DAYS_IN_MONTH = 31;

const range = (length: number, from = 1): SelectOption[] =>
	Array.from({ length }, (_, index) => {
		const value = String(index + from);
		return { value, text: value };
	});

/**
 * These builders are called from JSX on every keystroke in the account forms.
 * Their inputs change rarely (locale, country list, month/year), so results are
 * cached at module level rather than re-derived per render: `getYearsOptions`
 * allocated ~96 objects and sorted them, `getLocaleMonthOptions` constructed an
 * Intl.DateTimeFormat plus 12 Dates, and `getCountriesNormalize` sorted the
 * full ~250-entry country list.
 */

const dayOptionsCache = new Map<string, SelectOption[]>();
const monthOptionsCache = new Map<string, SelectOption[]>();
const countryOptionsCache = new Map<string, SelectOption[]>();

let yearOptionsCache: { year: number; options: SelectOption[] } | null = null;

const ALL_DAYS = range(MAX_DAYS_IN_MONTH);

export const getDayOptionsForMonthYear = (
	monthStr: string,
	yearStr: string
): SelectOption[] => {
	const month = parseInt(monthStr, 10);
	const year = parseInt(yearStr, 10);

	if (isNaN(month) || month < 1 || month > 12 || isNaN(year) || year < 1) {
		return ALL_DAYS;
	}

	const key = `${year}-${month}`;
	const cached = dayOptionsCache.get(key);
	if (cached) return cached;

	// Day 0 of the next month is the last day of this one.
	const daysInMonth = new Date(year, month, 0).getDate();
	const options = range(daysInMonth);

	dayOptionsCache.set(key, options);
	return options;
};

export const getLocaleMonthOptions = (
	locale: string = typeof navigator !== 'undefined'
		? navigator.language
		: 'en'
): SelectOption[] => {
	const cached = monthOptionsCache.get(locale);
	if (cached) return cached;

	const formatter = new Intl.DateTimeFormat(locale, { month: 'long' });
	const year = new Date().getFullYear();

	const options = Array.from({ length: 12 }, (_, index) => ({
		value: String(index + 1),
		text: formatter.format(new Date(year, index, 1)),
	}));

	monthOptionsCache.set(locale, options);
	return options;
};

export const getYearsOptions = (): SelectOption[] => {
	const currentYear = new Date().getFullYear();

	if (yearOptionsCache?.year === currentYear) return yearOptionsCache.options;

	const options = range(
		currentYear - FIRST_BIRTH_YEAR + 1,
		FIRST_BIRTH_YEAR
	).reverse();

	yearOptionsCache = { year: currentYear, options };
	return options;
};

type CountryField = 'shortName' | 'fullName';

/**
 * Country options sorted alphabetically with `priorityCode` pinned first.
 * Cached per (country list identity, output field, priority) because the list
 * arrives once per page load and never changes afterwards.
 */
export const getCountriesNormalize = (
	countries: Country[],
	outputType: CountryField,
	priorityCode: string
): SelectOption[] => {
	if (!countries?.length) return [];

	const key = `${countries.length}:${countries[0]?.shortName}:${outputType}:${priorityCode}`;
	const cached = countryOptionsCache.get(key);
	if (cached) return cached;

	const priorityName = countries.find(
		(country) => country?.shortName === priorityCode
	)?.fullName;

	const options = countries
		.map((country) => ({
			value: String(country[outputType]),
			text: String(country.fullName),
		}))
		.sort((a, b) => {
			if (a.text === priorityName) return -1;
			if (b.text === priorityName) return 1;
			return a.text.localeCompare(b.text, undefined, {
				sensitivity: 'base',
				numeric: true,
			});
		});

	countryOptionsCache.set(key, options);
	return options;
};
